import { type LineItem, type OrderPricingMetadata } from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import type { MinimalShipmentFee } from '@alto/scriptdash/alto/scheduling/types/v1/minimal_shipment_fee';
import { useQuery } from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { staleTimes } from './constants';
import { queries } from './query-keys';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import {
  getIsOrderPayAtPickup,
  getIsOrderPickupMethod,
  getOrderAddressId,
  getOrderDate,
  getOrderDeliverAfter,
  getOrderDeliverBefore,
  getOrderDeliverMethod,
  getOrderIsAsap,
  getOrderOnDemandDeliveryPrice,
  getOrderTipAmount,
  getSaveOrderLoading,
} from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { makeFetchOrderPricingOrderParam } from '~shared/features/pricing/helpers';
import { type ItemPricing } from '~shared/features/pricing/types';
import { getIsShipmentIDKnownInClientState } from '~shared/features/shipments/selectors/getIsShipmentIDKnownInClientState';
import getIsAuthorized from '~shared/selectors/auth/getIsAuthorized';
import { useSelectorShared } from '~shared/store';

export type OrderPricingQueryData = {
  has_copay: boolean;
  item_pricing: ItemPricing;
  line_items: LineItem[];
  metadata: OrderPricingMetadata;
  price_unconfirmed: boolean;
  subtotal: number | null;
  total: LineItem | undefined;
};

export type OrderPricing = {
  appliedShipmentFees: MinimalShipmentFee[];
  hasAppliedDeliveryFee: boolean;
  isError: boolean;
  isOrderPricingLoading: boolean;
  isPaymentMethodRequired: boolean;
  isSuccess: boolean;
  lastFetchedAt: number;
  orderPricing: OrderPricingQueryData | undefined;
};

type Props = {
  shipmentID?: number;
  bundlingOptionShipmentID?: number;
};

export const useOrderPricing = ({ shipmentID, bundlingOptionShipmentID }: Props = {}) => {
  const { cartItems, selectedPaymentTypes } = useCartData();
  const hasCartItems = cartItems.length > 0;
  const { cartItemsByDeliveryMethod, deliveryMethods } = useCartDeliveryMethods();
  const orderIndex = useSelectorShared(getOrderIndex);
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const isSaveOrderLoading = useSelectorShared(getSaveOrderLoading);
  const isShipmentKnownInClientState = useSelectorShared((state) =>
    getIsShipmentIDKnownInClientState(state, shipmentID),
  );

  // Subscribe only to the pricing-relevant order fields so irrelevant updateOrder dispatches
  // (e.g. home_to_sign_confirmation, payment_method_id) do not re-render this hook.
  const addressId = useSelectorShared(getOrderAddressId);
  const date = useSelectorShared(getOrderDate);
  const deliverAfter = useSelectorShared(getOrderDeliverAfter);
  const deliverBefore = useSelectorShared(getOrderDeliverBefore);
  const deliveryMethod = useSelectorShared(getOrderDeliverMethod);
  const isOrderPickupMethod = useSelectorShared(getIsOrderPickupMethod);
  const isOrderPayAtPickup = useSelectorShared(getIsOrderPayAtPickup);
  const isAsap = useSelectorShared(getOrderIsAsap);
  const onDemandDeliveryPrice = useSelectorShared(getOrderOnDemandDeliveryPrice);
  const tipAmount = useSelectorShared(getOrderTipAmount);

  // For split shipments, limit to cart items for the current order so the pricing request
  // has one delivery_method_type and only the items for that shipment (avoids wrong fees e.g. next_day_mail).
  const cartItemsForPricing = useMemo(() => {
    if (shipmentID || bundlingOptionShipmentID) return cartItems;
    if (deliveryMethods.length <= 1) return cartItems;
    const deliveryMethodForOrder = deliveryMethods[orderIndex];
    const deliveryMethodName = deliveryMethodForOrder?.name;
    const itemsForShipment = deliveryMethodName ? (cartItemsByDeliveryMethod[deliveryMethodName] ?? []) : [];
    return itemsForShipment.length > 0 ? itemsForShipment : cartItems;
  }, [cartItems, cartItemsByDeliveryMethod, deliveryMethods, orderIndex, shipmentID, bundlingOptionShipmentID]);

  // Build pricingOrder from primitive selector values — all deps are scalars so useMemo
  // only re-runs when a pricing-relevant field actually changes.
  const pricingOrder = useMemo(
    () =>
      makeFetchOrderPricingOrderParam(
        {
          address_id: addressId,
          date,
          deliver_after: deliverAfter,
          deliver_before: deliverBefore,
          delivery_method: deliveryMethod,
          is_asap: isAsap,
          on_demand_delivery_price: onDemandDeliveryPrice,
          tip_amount: tipAmount,
        },
        cartItemsForPricing,
        selectedPaymentTypes,
      ),
    [
      addressId,
      date,
      deliverAfter,
      deliverBefore,
      deliveryMethod,
      isAsap,
      onDemandDeliveryPrice,
      tipAmount,
      cartItemsForPricing,
      selectedPaymentTypes,
    ],
  );

  // Only enable the query if:
  // 1. User is authorized
  // 2. AND one of:
  //    - We have a shipmentID (pricing existing shipment)
  //    - We have a bundlingOptionShipmentID
  //    - We have items in the pricing order (cart has items)
  const isQueryEnabled = useMemo(() => {
    if (!isAuthorized) return false;
    if (shipmentID && !isShipmentKnownInClientState) return false;
    if (shipmentID || bundlingOptionShipmentID) return true;
    return hasCartItems;
  }, [bundlingOptionShipmentID, hasCartItems, isAuthorized, isShipmentKnownInClientState, shipmentID]);

  // While POST /deliveries is in flight, checkout Redux/cart can change in ways that alter the pricing query key
  // and produce misleading refetches (e.g. delivery fee → Free). Pause cart-based pricing only; shipment/bundling
  // pricing does not depend on the checkout cart snapshot.
  const isPricingQueryPausedForSaveOrder = isSaveOrderLoading && !shipmentID && !bundlingOptionShipmentID;

  const { data, isError, isLoading, isRefetching, isSuccess, refetch, dataUpdatedAt } = useQuery({
    ...queries.pricing.order({
      shipmentID: shipmentID ? shipmentID : undefined,
      order: shipmentID ? undefined : pricingOrder,
      bundlingOptionShipmentID,
    }),
    enabled: isQueryEnabled && !isPricingQueryPausedForSaveOrder,
    staleTime: staleTimes.fiveMinutes,
    placeholderData: (previousData) => previousData,
  });

  /**
   * Determine if a payment method is required for medications only.
   * If the price is unavailable (i.e. billing issue) or the subtotal
   * is zero, then return false. Otherwise, return true.
   */
  const isPaymentMethodRequired = useMemo(() => {
    if (isOrderPickupMethod && isOrderPayAtPickup) {
      return false;
    }

    if (!data?.total) {
      return true;
    }

    return (data?.subtotal ?? 0) > 0 || data?.total.info_key === 'price_unavailable';
  }, [isOrderPickupMethod, isOrderPayAtPickup, data?.total, data?.subtotal]);

  const appliedShipmentFees = useMemo(() => {
    if (!data) {
      return [];
    }

    const feeTypeMap: Record<string, MinimalShipmentFee['fee_type']> = {
      essentials_only_fee: 'essentials_only_delivery',
      same_day_delivery_fee: 'same_day_delivery',
      next_day_delivery_fee: 'next_day_delivery',
      standard_delivery_fee: 'standard_delivery',
      on_demand_fee: 'on_demand_delivery',
      asap_delivery_fee: 'asap_delivery',
      next_day_mail_fee: 'next_day_mail',
      standard_mail_fee: 'standard_mail',
    };

    return data.line_items
      .filter((lineItem) => feeTypeMap[lineItem.key])
      .map((lineItem) => ({
        fee_type: feeTypeMap[lineItem.key],
        amount: lineItem.value?.toString() || '',
      }));
  }, [data]);

  const appliedDeliveryFee = useMemo(() => {
    const appliedFeesByType = keyBy(appliedShipmentFees, 'fee_type');

    const sameDayDeliveryFee = appliedFeesByType['same_day_delivery'];
    const nextDayDeliveryFee = appliedFeesByType['next_day_delivery'];
    const standardDeliveryFee = appliedFeesByType['standard_delivery'];
    const asapDeliveryFee = appliedFeesByType['asap_delivery'];
    const nextDayMailFee = appliedFeesByType['next_day_mail'];
    const standardMailFee = appliedFeesByType['standard_mail'];

    return (
      sameDayDeliveryFee ||
      nextDayDeliveryFee ||
      standardDeliveryFee ||
      asapDeliveryFee ||
      nextDayMailFee ||
      standardMailFee
    );
  }, [appliedShipmentFees]);

  return {
    appliedShipmentFees,
    hasAppliedDeliveryFee: !!appliedDeliveryFee && parseFloat(appliedDeliveryFee.amount) > 0,
    isError,
    isOrderPricingLoading: isLoading || isRefetching,
    isPaymentMethodRequired,
    isSuccess,
    lastFetchedAt: dataUpdatedAt,
    orderPricing: data,
    refetchOrderPricing: refetch,
  };
};
